/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Widget from './Widget';
import { getCurrencyNarratives } from '../../api/api';

const TIMEFRAMES = ['daily', 'weekly', '14d', '30d'];

const SentimentBadge = ({ score }) => {
  const s = parseFloat(score) || 0;
  const label = s > 0.2 ? 'Bullish' : s < -0.2 ? 'Bearish' : 'Neutral';
  const color =
    s > 0.2
      ? 'bg-success/15 text-success'
      : s < -0.2
        ? 'bg-destructive/15 text-destructive'
        : 'bg-secondary text-textSecondary';
  return (
    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${color}`}>
      {label} ({(s * 100).toFixed(0)}%)
    </span>
  );
};

const CurrencyNarrativesWidget = ({
  hideTitle = false,
  limit = 20,
  autoRefreshMs = 120000,
}) => {
  const [narratives, setNarratives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('daily');
  const [expandedId, setExpandedId] = useState(null);

  const fetchNarratives = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCurrencyNarratives({ timeframe });
      const arr = Array.isArray(data) ? data : [];
      setNarratives(arr.slice(0, limit));
    } catch (err) {
      console.error('Error fetching currency narratives:', err);
      setError('Failed to load narratives');
    } finally {
      setLoading(false);
    }
  }, [timeframe, limit]);

  useEffect(() => {
    fetchNarratives();

    if (autoRefreshMs > 0) {
      const interval = setInterval(fetchNarratives, autoRefreshMs);
      return () => clearInterval(interval);
    }
  }, [fetchNarratives, autoRefreshMs]);

  const sortedNarratives = useMemo(() => {
    return [...narratives].sort((a, b) => {
      const confDiff = (b.confluence_score || 0) - (a.confluence_score || 0);
      if (Math.abs(confDiff) > 0.1) return confDiff;
      return Math.abs(b.sentiment_score || 0) - Math.abs(a.sentiment_score || 0);
    });
  }, [narratives]);

  const getBiasColor = (bias) => {
    if (!bias) return 'text-textSecondary';
    if (bias === 'bullish') return 'text-success';
    if (bias === 'bearish') return 'text-destructive';
    return 'text-textSecondary';
  };

  return (
    <Widget title="Currency Narratives" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {/* Timeframe selector + refresh */}
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-borderDefault flex-shrink-0">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-2 py-0.5 text-[10px] sm:text-xs rounded transition-colors ${
                timeframe === tf
                  ? 'bg-primary text-white font-semibold'
                  : 'text-textSecondary hover:bg-secondary/30'
              }`}
            >
              {tf}
            </button>
          ))}
          <div className="flex-1" />
          <span className="text-[10px] text-textSecondary mr-1">
            {sortedNarratives.length}
          </span>
          <button
            onClick={fetchNarratives}
            disabled={loading}
            className="text-[10px] sm:text-xs text-primary hover:text-primary/80 disabled:opacity-50"
          >
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 text-center text-destructive text-xs">{error}</div>
        )}

        {/* Empty state */}
        {!error && !loading && sortedNarratives.length === 0 && (
          <div className="p-4 text-center text-textSecondary text-sm">
            No narratives for this timeframe
          </div>
        )}

        {/* Narrative list */}
        <div className="flex-1 overflow-y-auto">
          {sortedNarratives.map((n) => {
            const isExpanded = expandedId === n.id;
            const confPct = Math.round((n.confluence_score || 0) * 100);
            const confColor =
              confPct >= 70
                ? 'text-success'
                : confPct >= 40
                  ? 'text-yellow-500'
                  : 'text-destructive';

            return (
              <div
                key={n.id}
                className="border-b border-borderDefault"
              >
                {/* Clickable header row */}
                <div
                  className="px-2 sm:px-3 py-2 hover:bg-secondary/10 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : n.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-bold text-textPrimary">{n.currency}</span>
                      <SentimentBadge score={n.sentiment_score} />
                      {n.technical_bias && (
                        <span
                          className={`text-[10px] font-semibold uppercase px-1 py-0.5 rounded ${
                            n.technical_bias === 'bullish'
                              ? 'bg-success/15 text-success'
                              : n.technical_bias === 'bearish'
                                ? 'bg-destructive/15 text-destructive'
                                : 'bg-secondary text-textSecondary'
                          }`}
                        >
                          TA: {n.technical_bias}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 text-[10px] sm:text-xs text-textSecondary">
                      {confPct > 0 && (
                        <span className={`font-bold ${confColor}`}>{confPct}%</span>
                      )}
                      <span>{n.event_count} evts</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-2 sm:px-3 pb-3 space-y-3 bg-secondary/5 overflow-hidden min-w-0">
                    {/* Summary */}
                    {n.summary && (
                      <p className="text-xs text-textSecondary leading-relaxed whitespace-pre-line break-words overflow-hidden">
                        {n.summary}
                      </p>
                    )}

                    {/* Confluence bar */}
                    {confPct > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-textSecondary uppercase font-medium">Confluence</span>
                        <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              confPct >= 70 ? 'bg-success' : confPct >= 40 ? 'bg-yellow-500' : 'bg-destructive'
                            }`}
                            style={{ width: `${confPct}%` }}
                          />
                        </div>
                        <span className={`text-[10px] font-bold ${confColor}`}>{confPct}%</span>
                      </div>
                    )}

                    {/* Outlook */}
                    {n.outlook && Object.keys(n.outlook).length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase text-textSecondary font-medium mb-1">Outlook</p>
                        <div className="space-y-1">
                          {['short_term', 'medium_term', 'long_term'].map((horizon) => {
                            const data = n.outlook[horizon];
                            if (!data) return null;
                            const bias = data.bias || 'neutral';
                            const conf = Math.round((data.confidence || 0) * 100);
                            const label = horizon.replace('_', '-');
                            return (
                              <div key={horizon} className="flex items-center gap-2 text-xs">
                                <span className="w-20 text-textSecondary capitalize">{label}</span>
                                <span className={`font-bold ${getBiasColor(bias)}`}>
                                  {bias === 'bullish' ? '▲' : bias === 'bearish' ? '▼' : '●'} {bias}
                                </span>
                                <span className="text-textSecondary">({conf}%)</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* COT positioning */}
                    {n.cot_positioning && Object.keys(n.cot_positioning).length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase text-textSecondary font-medium mb-1">COT Positioning</p>
                        <div className="text-xs text-textSecondary space-y-0.5">
                          {Object.entries(n.cot_positioning)
                            .filter(([, v]) => !Array.isArray(v) && (typeof v !== 'object' || v === null))
                            .map(([k, v]) => (
                              <div key={k} className="flex gap-2">
                                <span className="capitalize">{k.replace(/_/g, ' ')}:</span>
                                <span className="text-textPrimary">{typeof v === 'number' ? v.toFixed(1) : String(v ?? '')}</span>
                              </div>
                            ))}
                        </div>
                        {/* Weeks data table */}
                        {Array.isArray(n.cot_positioning.weeks_data) && n.cot_positioning.weeks_data.length > 0 && (
                          <div className="mt-1.5">
                            <span className="text-[10px] text-textSecondary">Weekly trend:</span>
                            <div className="flex gap-1 mt-0.5">
                              {n.cot_positioning.weeks_data.map((w, i) => {
                                const net = w.net_noncomm ?? w.net ?? null;
                                const prev = i > 0 ? (n.cot_positioning.weeks_data[i - 1].net_noncomm ?? n.cot_positioning.weeks_data[i - 1].net ?? null) : null;
                                const up = prev != null && net != null && net > prev;
                                const down = prev != null && net != null && net < prev;
                                return (
                                  <span
                                    key={i}
                                    title={w.date ? `${w.date}: ${net}` : `Week ${i + 1}: ${net}`}
                                    className={`inline-block w-5 h-5 rounded text-center text-[9px] leading-5 font-mono ${
                                      up ? 'bg-success/20 text-success' : down ? 'bg-destructive/20 text-destructive' : 'bg-secondary text-textSecondary'
                                    }`}
                                  >
                                    {up ? '▲' : down ? '▼' : '●'}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Key drivers */}
                    {n.key_drivers && n.key_drivers.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase text-textSecondary font-medium mb-1">Key Drivers</p>
                        <ul className="text-xs text-textSecondary space-y-0.5 list-disc list-inside">
                          {n.key_drivers.slice(0, 5).map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Widget>
  );
};

CurrencyNarrativesWidget.propTypes = {
  hideTitle: PropTypes.bool,
  limit: PropTypes.number,
  autoRefreshMs: PropTypes.number,
};

export default CurrencyNarrativesWidget;
